import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Label} from '@/components/ui/label'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {PorQue} from '@/components/ui/por-que'
import {useRetirarPaquete, type BateriaPaquete, type RetiroMotivo} from '@/hooks/bateria/use-bateria'
import {translateApiError} from '@/lib/errors'
import {validateRequired} from '@/lib/validation'

const MOTIVOS: RetiroMotivo[] = ['no_asistio', 'renuncio', 'duplicado', 'error_de_alta', 'otro']

/** Con respuestas leídas nada se destruye: el registro clínico queda bajo custodia de Inci (Res.
 * 2764/2022) y el motivo es obligatorio. G23: el motivo tipado alimenta la cobertura del informe. */
export function RetirarPaqueteDialog({
  businessId,
  evaluacionId,
  paquetes,
  open,
  onOpenChange,
}: {
  businessId: string
  evaluacionId: string
  paquetes: BateriaPaquete[]
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const {t} = useTranslation()
  const retirar = useRetirarPaquete(businessId, evaluacionId)
  // Ref de callback: Radix monta el contenido del diálogo después del primer efecto (ver `popover.tsx`).
  const [nodoRetirar, setNodoRetirar] = useState<HTMLElement | null>(null)
  const [motivo, setMotivo] = useState<RetiroMotivo | null>(null)
  const [detalle, setDetalle] = useState('')
  const [motivoError, setMotivoError] = useState<string | null>(null)
  const [detalleError, setDetalleError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [fallidos, setFallidos] = useState<{nombre: string; detalle: string}[]>([])
  const [pending, setPending] = useState(false)

  const conDatos = paquetes.some((p) => p.tiene_datos)
  const multiple = paquetes.length > 1

  function close() {
    setMotivo(null)
    setDetalle('')
    setMotivoError(null)
    setDetalleError(null)
    setError(null)
    setFallidos([])
    retirar.reset()
    onOpenChange(false)
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const problemaMotivo = conDatos ? validateRequired(motivo ?? '') : null
    const problemaDetalle = motivo === 'otro' ? validateRequired(detalle) : null
    setMotivoError(problemaMotivo)
    setDetalleError(problemaDetalle)
    if (problemaMotivo || problemaDetalle) return
    setPending(true)
    const fallos: {nombre: string; detalle: string}[] = []
    for (const paquete of paquetes) {
      try {
        await retirar.mutateAsync({paqueteId: paquete.id, motivo, motivoDetalle: detalle.trim() || null})
      } catch (err) {
        fallos.push({nombre: paquete.employee_name, detalle: translateApiError(err)})
      }
    }
    setPending(false)
    setFallidos(fallos)
    if (fallos.length === 0) close()
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent ref={setNodoRetirar} className="max-w-lg" data-testid="retirar-dialog">
        <DialogHeader>
          <DialogTitle>
            {multiple
              ? t('admin.bateria.retirar.titleMultiple', {count: paquetes.length})
              : t('admin.bateria.retirar.title')}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          {multiple ? (
            <ul className="max-h-32 space-y-0.5 overflow-y-auto text-sm text-foreground">
              {paquetes.map((p) => (
                <li key={p.id} className="truncate">
                  {p.employee_name}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-foreground">
              {t('admin.bateria.retirar.intro', {nombre: paquetes[0]?.employee_name})}
            </p>
          )}
          <div
            className={conDatos ? 'notice-amber rounded-lg px-4 py-2.5 text-sm' : 'text-sm text-muted-foreground'}
            data-testid={conDatos ? 'retirar-aviso-datos' : 'retirar-aviso-limpio'}>
            {conDatos ? t('admin.bateria.retirar.avisoConDatos') : t('admin.bateria.retirar.avisoSinDatos')}
            {conDatos && (
              <PorQue
                label={t('admin.bateria.retirar.porqueLabel')}
                contenedor={nodoRetirar}
                testId="retirar-porque"
                contenidoTestId="retirar-porque-contenido">
                {t('admin.bateria.retirar.porque')}
              </PorQue>
            )}
          </div>
          <div className="space-y-2">
            <Label>{conDatos ? t('admin.bateria.retirar.motivo') : t('admin.bateria.retirar.motivoOpcional')}</Label>
            <div className="space-y-1.5">
              {MOTIVOS.map((value) => (
                <label key={value} className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="radio"
                    name="retirar-motivo"
                    checked={motivo === value}
                    onChange={() => {
                      setMotivo(value)
                      setMotivoError(null)
                    }}
                    data-testid={`retirar-motivo-${value}`}
                    className="accent-primary"
                  />
                  {t(`admin.bateria.retirar.motivoOpcion.${value}`)}
                </label>
              ))}
            </div>
            <FieldError error={motivoError ?? undefined} />
          </div>
          {motivo === 'otro' && (
            <div className="space-y-2">
              <Label htmlFor="retirar-motivo-detalle">{t('admin.bateria.retirar.motivoDetalle')}</Label>
              <textarea
                id="retirar-motivo-detalle"
                data-testid="retirar-motivo-detalle"
                value={detalle}
                onChange={(e) => {
                  setDetalle(e.target.value)
                  setDetalleError(null)
                }}
                onBlur={() => setDetalleError(validateRequired(detalle))}
                aria-invalid={!!detalleError}
                className="min-h-16 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm text-foreground"
                placeholder={t('admin.bateria.retirar.motivoDetallePlaceholder')}
              />
              <FieldError error={detalleError ?? undefined} />
            </div>
          )}
          {fallidos.length > 0 && (
            <div className="notice-amber rounded-lg px-4 py-2.5 text-sm" data-testid="retirar-fallidos">
              <p className="font-medium">{t('admin.bateria.retirar.fallidos', {count: fallidos.length})}</p>
              <ul className="mt-1 space-y-0.5 text-xs opacity-90">
                {fallidos.map((f) => (
                  <li key={f.nombre}>
                    {f.nombre}: {f.detalle}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <FormDialogFooter
            error={error}
            pending={pending}
            submitLabel={
              multiple
                ? t('admin.bateria.retirar.submitMultiple', {count: paquetes.length})
                : t('admin.bateria.retirar.submit')
            }
            onCancel={close}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}
